import { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Typography,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Autocomplete,
  Stack,
  Snackbar,
  Alert
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { AgendaPanel } from '../components/AgendaPanel';
import { GeocodingCheckinDialog } from '../components/GeocodingCheckinDialog';
import {
  useAgenda,
  useCriarAgendaVisita,
  useReordenarAgenda,
  useCheckinAgenda,
  useCheckoutAgenda,
  localDateString
} from '../hooks/useAgenda';
import type { AgendaVisita } from '../hooks/useAgenda';
import { useClientes } from '../../../hooks/useClientes';
import type { AxiosError } from 'axios';

// AIDEV-NOTE: Extrai a mensagem de erro do body da resposta axios (campo "error") ou fallback para msg genérica
function extractApiError(err: unknown): string {
  const axiosErr = err as AxiosError<{ error?: string }>;
  return (
    axiosErr?.response?.data?.error ?? (err instanceof Error ? err.message : 'Erro desconhecido')
  );
}

// AIDEV-NOTE: Tipos de visita disponiveis para criacao na agenda
const TIPOS_VISITA = [
  { value: 'prospeccao', label: 'Prospecao' },
  { value: 'manutencao', label: 'Manutencao' },
  { value: 'cobranca', label: 'Cobranca' },
  { value: 'entrega', label: 'Entrega' },
  { value: 'outro', label: 'Outro' }
] as const;

// AIDEV-NOTE: Data local do dispositivo — toISOString() usava o dia UTC (errado após 21h locais)
function formatDateInput(date: Date): string {
  return localDateString(date);
}

function addDays(dateStr: string, days: number): string {
  const d = new Date(dateStr + 'T12:00:00');
  d.setDate(d.getDate() + days);
  return formatDateInput(d);
}

export function AgendaPage() {
  const [dataSelecionada, setDataSelecionada] = useState(formatDateInput(new Date()));
  const [dialogOpen, setDialogOpen] = useState(false);
  const [novaVisita, setNovaVisita] = useState({
    cliente_id: '',
    cliente_nome: '',
    data_planejada: '',
    tipo_visita: 'manutencao' as string,
    observacoes: ''
  });
  const [pendingCheckinId, setPendingCheckinId] = useState<string | null>(null);
  const [pendingCheckoutId, setPendingCheckoutId] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success'
  });
  // AIDEV-NOTE: Guarda a visita em fila para checkin após geocodificação bem-sucedida
  const [geocodingVisita, setGeocodingVisita] = useState<AgendaVisita | null>(null);

  const { data: agenda = [], isLoading } = useAgenda(dataSelecionada);
  const criarVisita = useCriarAgendaVisita();
  const reordenar = useReordenarAgenda();
  const checkinMutation = useCheckinAgenda();
  const checkoutMutation = useCheckoutAgenda();

  const handlePrevDay = () => setDataSelecionada((d) => addDays(d, -1));
  const handleNextDay = () => setDataSelecionada((d) => addDays(d, 1));

  const handleOpenDialog = () => {
    setNovaVisita({
      cliente_id: '',
      cliente_nome: '',
      data_planejada: `${dataSelecionada}T09:00`,
      tipo_visita: 'manutencao',
      observacoes: ''
    });
    setDialogOpen(true);
  };

  const handleCreateVisita = async () => {
    if (!novaVisita.cliente_id) return;
    // AIDEV-NOTE: Converte o datetime-local (fuso do dispositivo) para instante UTC real.
    // Enviar a string sem fuso fazia o server (UTC) gravar a hora deslocada em -3h.
    await criarVisita.mutateAsync({
      cliente_id: novaVisita.cliente_id,
      data_planejada: new Date(novaVisita.data_planejada).toISOString(),
      tipo_visita: novaVisita.tipo_visita,
      observacoes: novaVisita.observacoes || undefined,
      ordem_dia: agenda.length + 1
    });
    setDialogOpen(false);
  };

  const handleReorder = useCallback(
    (reordered: AgendaVisita[]) => {
      reordenar.mutate({
        data: dataSelecionada,
        ordem: reordered.map((v) => ({ id: v.id, ordem_dia: v.ordem_dia }))
      });
    },
    [dataSelecionada, reordenar]
  );

  // AIDEV-NOTE: Obtém GPS e executa checkin. Usado tanto na primeira tentativa quanto no retry
  // após geocodificação bem-sucedida. Retorna promise para permitir encadeamento.
  const executarCheckin = useCallback(
    (visita: AgendaVisita): Promise<void> =>
      new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            checkinMutation.mutate(
              {
                cliente_id: visita.cliente_id,
                agenda_visita_id: visita.id,
                lat: pos.coords.latitude,
                lng: pos.coords.longitude,
                accuracy: pos.coords.accuracy
              },
              {
                onSuccess: () => resolve(),
                onError: (err) => reject(new Error(extractApiError(err)))
              }
            );
          },
          () => reject(new Error('Não foi possível obter sua localização. Ative o GPS.')),
          { enableHighAccuracy: true, timeout: 10000 }
        );
      }),
    [checkinMutation]
  );

  const handleCheckin = useCallback(
    (visita: AgendaVisita) => {
      setPendingCheckinId(visita.id);
      executarCheckin(visita)
        .then(() => {
          setPendingCheckinId(null);
          setSnackbar({
            open: true,
            message: 'Check-in realizado com sucesso!',
            severity: 'success'
          });
        })
        .catch((err: unknown) => {
          setPendingCheckinId(null);
          const msg = err instanceof Error ? err.message : 'Erro ao fazer check-in';
          // AIDEV-NOTE: Abre dialog de geocodificação apenas para erro de coordenadas ausentes
          if (msg.includes('sem coordenadas') || msg.includes('coordenadas GPS')) {
            setGeocodingVisita(visita);
          } else {
            setSnackbar({ open: true, message: msg, severity: 'error' });
          }
        });
    },
    [executarCheckin]
  );

  // AIDEV-NOTE: Obtém GPS, chama endpoint de checkout e atualiza status da agenda
  const handleCheckout = useCallback(
    (visita: AgendaVisita) => {
      if (!visita.visita_id) return;
      setPendingCheckoutId(visita.id);
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          checkoutMutation.mutate(
            {
              visita_id: visita.visita_id!,
              lat: pos.coords.latitude,
              lng: pos.coords.longitude,
              accuracy: pos.coords.accuracy
            },
            {
              onSuccess: () => {
                setPendingCheckoutId(null);
                setSnackbar({ open: true, message: 'Check-out registrado!', severity: 'success' });
              },
              onError: (err) => {
                setPendingCheckoutId(null);
                setSnackbar({ open: true, message: extractApiError(err), severity: 'error' });
              }
            }
          );
        },
        () => {
          setPendingCheckoutId(null);
          setSnackbar({
            open: true,
            message: 'Não foi possível obter sua localização. Ative o GPS.',
            severity: 'error'
          });
        },

        { enableHighAccuracy: true, timeout: 10000 }
      );
    },
    [checkoutMutation]
  );

  // Busca de clientes com debounce para o autocomplete
  const [clienteBusca, setClienteBusca] = useState('');
  const [clienteBuscaDebounced, setClienteBuscaDebounced] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setClienteBuscaDebounced(clienteBusca), 300);
    return () => clearTimeout(timer);
  }, [clienteBusca]);

  const { data: clientesData = [] } = useClientes(
    clienteBuscaDebounced.length >= 2
      ? { busca: clienteBuscaDebounced, situacao: 'A' }
      : { situacao: 'A' }
  );
  // AIDEV-NOTE: Usa UUID (sync_clientes.id) — agenda_visitas.cliente_id é UUID
  const clienteOptions = clientesData
    .filter((c) => c.id)
    .map((c) => ({ id: c.id!, nome: c.razao_social }));

  const dataFormatada = new Date(dataSelecionada + 'T12:00:00').toLocaleDateString('pt-BR', {
    weekday: 'long',
    day: '2-digit',
    month: 'long',
    year: 'numeric'
  });

  return (
    <Box p={3}>
      {/* Seletor de data */}
      <Box display="flex" alignItems="center" justifyContent="center" gap={2} mb={3}>
        <IconButton onClick={handlePrevDay} aria-label="dia anterior">
          <ChevronLeftIcon />
        </IconButton>
        <Box textAlign="center">
          <Typography variant="h6">{dataFormatada}</Typography>
          <TextField
            type="date"
            value={dataSelecionada}
            onChange={(e) => setDataSelecionada(e.target.value)}
            size="small"
            sx={{ mt: 0.5 }}
            inputProps={{ style: { textAlign: 'center' } }}
          />
        </Box>
        <IconButton onClick={handleNextDay} aria-label="proximo dia">
          <ChevronRightIcon />
        </IconButton>
      </Box>

      {/* Painel da agenda */}
      <Box maxWidth={480} mx="auto" border={1} borderColor="divider" borderRadius={2} height="60vh">
        {isLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" height="100%">
            <Typography color="text.secondary">Carregando agenda...</Typography>
          </Box>
        ) : (
          <AgendaPanel
            visitas={agenda}
            onAddVisita={handleOpenDialog}
            onReorder={handleReorder}
            onCheckin={handleCheckin}
            onCheckout={handleCheckout}
            checkinPendingId={pendingCheckinId}
            checkoutPendingId={pendingCheckoutId}
          />
        )}
      </Box>

      {/* Snackbar de feedback de check-in/out */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Dialog de geocodificação para clientes sem coordenadas */}
      {geocodingVisita && (
        <GeocodingCheckinDialog
          open
          clienteId={geocodingVisita.cliente_id}
          clienteNome={geocodingVisita.cliente_nome ?? `Cliente ${geocodingVisita.cliente_id}`}
          enderecoAtual={[geocodingVisita.cliente_endereco, geocodingVisita.cliente_cidade]
            .filter(Boolean)
            .join(', ')}
          onClose={() => setGeocodingVisita(null)}
          onSuccess={() => {
            // Geocodificação OK — retenta o checkin com as novas coordenadas
            const visita = geocodingVisita;
            setGeocodingVisita(null);
            setPendingCheckinId(visita.id);
            executarCheckin(visita)
              .then(() => {
                setPendingCheckinId(null);
                setSnackbar({
                  open: true,
                  message: 'Check-in realizado com sucesso!',
                  severity: 'success'
                });
              })
              .catch((err: unknown) => {
                setPendingCheckinId(null);
                const msg = err instanceof Error ? err.message : 'Erro ao fazer check-in';
                setSnackbar({ open: true, message: msg, severity: 'error' });
              });
          }}
        />
      )}

      {/* Dialog para nova visita */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Adicionar Visita a Agenda</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <Autocomplete
              options={clienteOptions}
              getOptionLabel={(option) => option.nome}
              onChange={(_e, value) => {
                setNovaVisita((prev) => ({
                  ...prev,
                  cliente_id: value?.id ?? '',
                  cliente_nome: value?.nome ?? ''
                }));
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Cliente"
                  size="small"
                  required
                  placeholder="Buscar cliente..."
                />
              )}
              onInputChange={(_e, value) => setClienteBusca(value)}
              freeSolo={false}
              noOptionsText={
                clienteBusca.length < 2 ? 'Digite ao menos 2 letras' : 'Nenhum cliente encontrado'
              }
            />

            <TextField
              type="datetime-local"
              label="Data e Hora"
              value={novaVisita.data_planejada}
              onChange={(e) =>
                setNovaVisita((prev) => ({ ...prev, data_planejada: e.target.value }))
              }
              size="small"
              fullWidth
              InputLabelProps={{ shrink: true }}
            />

            <FormControl size="small" fullWidth>
              <InputLabel>Tipo de Visita</InputLabel>
              <Select
                value={novaVisita.tipo_visita}
                label="Tipo de Visita"
                onChange={(e) =>
                  setNovaVisita((prev) => ({ ...prev, tipo_visita: e.target.value }))
                }
              >
                {TIPOS_VISITA.map((t) => (
                  <MenuItem key={t.value} value={t.value}>
                    {t.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Observacoes"
              value={novaVisita.observacoes}
              onChange={(e) => setNovaVisita((prev) => ({ ...prev, observacoes: e.target.value }))}
              size="small"
              fullWidth
              multiline
              rows={2}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleCreateVisita}
            disabled={!novaVisita.cliente_id || criarVisita.isPending}
          >
            {criarVisita.isPending ? 'Criando...' : 'Adicionar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
